import { getVoiceLabelInitials } from '@app/modules/diarization/speaker-utils';
import { PersonLane } from '@app/modules/persons/appearances/types';
import { PersonAvatar } from '@app/modules/persons/components/PersonAvatar';
import { Avatar } from '@mui/material';
import { usePersonLaneIdentity } from '../hooks/usePersonLaneIdentity';

type PersonLaneAvatarProps = {
  lane: PersonLane;
  /** Outer size in px (overrides PersonAvatar size tokens). */
  sizePx: number;
  /** Colored ring width around the avatar. */
  borderWidth?: number;
  /**
   * When true, unknown lanes use a dashed border (cast / lane chrome).
   * Default false.
   */
  dashedWhenUnknown?: boolean;
};

/**
 * Lane-colored avatar used across Personas track chrome.
 *
 * Shows profile image or face crop when available, otherwise voice-label
 * initials. Ring uses the lane color (dashed for unknowns when requested).
 * Returns null until a display name or unknown label is ready.
 */
export function PersonLaneAvatar({
  lane,
  sizePx,
  borderWidth = 2,
  dashedWhenUnknown = false,
}: PersonLaneAvatarProps) {
  const { person, avatarSrc, fallbackName, isIdentityReady } = usePersonLaneIdentity(lane);

  if (!isIdentityReady) {
    return null;
  }

  const avatarSx = {
    width: sizePx,
    height: sizePx,
    bgcolor: lane.color,
    flexShrink: 0,
    boxSizing: 'border-box' as const,
    ...(borderWidth > 0
      ? {
          border: `${borderWidth}px solid`,
          borderColor: lane.color,
          ...(dashedWhenUnknown && lane.isUnknown ? { borderStyle: 'dashed' as const } : null),
        }
      : dashedWhenUnknown && lane.isUnknown
        ? { border: '1px dashed', borderColor: 'text.disabled' }
        : null),
  };

  // Prefer PersonAvatar when we have a person id or a crop src (unknown faces).
  if (lane.personId || avatarSrc) {
    return (
      <PersonAvatar
        person={person}
        fallbackName={fallbackName}
        src={avatarSrc}
        size="large"
        sx={avatarSx}
      />
    );
  }

  return (
    <Avatar sx={{ ...avatarSx, typography: 'body2' }}>
      {getVoiceLabelInitials(lane.fallbackLabel)}
    </Avatar>
  );
}
